import React, { useState } from 'react';
import { TeamCard, FeatureCard } from '@components/Card';
import styles from './ResourcesPage.module.css';

const featureDetails = [
  {
    title: 'Predictive Intelligence & Real-Time Monitoring',
    content: 'Our AI continuously analyzes your equipment data and fleet status, identifying patterns and anomalies before they become problems. Get instant alerts before something goes wrong, with context on severity, root cause, and business impact.',
  },
  {
    title: 'Instant Expert Knowledge at Your Fingertips',
    content: 'Forget digging through filing cabinets or waiting for the one person who "knows the system." Our AI chatbot is trained on your entire technical library—manuals, maintenance logs, past incidents, and more. Get the guidance you need in seconds, not hours.',
  },
  {
    title: 'Zero-Touch Work Order Generation',
    content: 'Why waste precious minutes manually creating tickets when the machine is already down? Laminar\'s AI agent automatically generates complete work orders the instant an issue is detected—complete with problem diagnosis, recommended solution, required parts, and technician assignment.',
  },
];
import vivekImg from '@assets/profiles/vivek.jpg';
import vinayImg from '@assets/profiles/vinay.jpg';
import keshavImg from '@assets/profiles/keshav.jpg';
import bartuImg from '@assets/profiles/bartu.jpg';
import malcolmImg from '@assets/profiles/malcolm.jpg';

const teamMembers = [
  { avatar: vivekImg, name: 'Vivek Chilakala', role: 'Financial Analyst', detail: 'Incoming analyst at Moelis', linkedinUrl: 'https://www.linkedin.com/in/vivek-chilakala-141222212/' },
  { avatar: vinayImg, name: 'Vinay Joshi', role: 'Energy Analyst', detail: 'Incoming analyst at Jefferies', linkedinUrl: 'https://www.linkedin.com/in/vinayjoshi2004/' },
  { avatar: keshavImg, name: 'Keshav Shah', role: 'Software Engineer', detail: 'Incoming software engineer at Stripe', linkedinUrl: 'https://www.linkedin.com/in/keshavkshah/' },
  { avatar: bartuImg, name: 'Bartu Citci', role: 'ML Researcher', detail: 'Applied machine learning and modeling', linkedinUrl: 'https://www.linkedin.com/in/bartucitci/' },
  { avatar: malcolmImg, name: 'Malcolm Wigder', role: 'Data Scientist', detail: 'Physics driven machine learning and systems modeling', linkedinUrl: 'https://www.linkedin.com/in/malcolm-wigder/' },
];

const solutions = [
  {
    title: 'AI-Powered Chatbot',
    description: 'Get instant answers from your technical manuals and documentation with our intelligent chatbot trained specifically on your compressor data.',
  },
  {
    title: 'Data Intelligence',
    description: 'Transform complex operational data into actionable insights with advanced AI analysis tailored for compressor operations.',
  },
  {
    title: 'Easy Integration',
    description: 'Seamlessly connect your existing data sources and start getting value from your information in minutes, not months.',
  },
  {
    title: 'Agentic Solutions',
    description: 'We know that every second counts. Laminar seeks out workflows that can be automated and does them for you. We hate busy work as much as you do.',
  },
];

const ResourcesPage: React.FC = () => {
  const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>({});

  const toggleExpand = (index: number) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <>
      {/* Page Header */}
      <section className="page-header">
        <h1 className="page-title">What We Do</h1>
        <p className="page-subtitle">Making maintenance data simple, accessible, and actionable.</p>
      </section>

      {/* Problem Section */}
      <section className={styles.resourcesSection}>
        <div className="container">
          <div className="section-header">
            <div className="section-label">The Problem</div>
            <h2 className="section-title">Every Second of Downtime Hurts</h2>
            <p className="section-subtitle">
              When critical equipment fails, your company becomes vulnerable. You risk losing revenue, violating contracts,
              and disappointing customers. With Laminar Labs, maintenance doesn't have to be chaotic. Our product aims to be
              proactive, accessible, and helpful.
            </p>
          </div>

          {/* Solution Flow Graphic */}
          <div className={styles.pipelineFlow}>
            <div className={styles.flowStage}>
              <div className={styles.flowIcon}>
                <svg viewBox="0 0 48 48" fill="none">
                  <circle cx="24" cy="24" r="14" fill="var(--color-gold)" opacity="0.15" stroke="var(--color-gold)" strokeWidth="2" />
                  <path d="M24 14 L24 26" stroke="var(--color-gold)" strokeWidth="3" strokeLinecap="round" />
                  <circle cx="24" cy="32" r="2" fill="var(--color-gold)" />
                </svg>
              </div>
              <h3 className={styles.flowLabel}>Predict</h3>
              <p className={styles.flowDesc}>AI detects issues before they happen</p>
            </div>

            <div className={styles.flowArrow}>
              <svg viewBox="0 0 80 24" fill="none">
                <line x1="0" y1="12" x2="60" y2="12" stroke="var(--color-gold)" strokeWidth="2" />
                <path d="M60 6 L74 12 L60 18" fill="var(--color-gold)" />
              </svg>
            </div>

            <div className={styles.flowStage}>
              <div className={styles.flowIcon}>
                <svg viewBox="0 0 48 48" fill="none">
                  <rect x="8" y="10" width="32" height="24" rx="4" fill="var(--color-gold)" opacity="0.15" stroke="var(--color-gold)" strokeWidth="2" />
                  <circle cx="16" cy="22" r="3" fill="var(--color-gold)" />
                  <line x1="22" y1="20" x2="36" y2="20" stroke="var(--color-text-secondary)" strokeWidth="2" strokeLinecap="round" />
                  <line x1="22" y1="26" x2="32" y2="26" stroke="var(--color-text-secondary)" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <h3 className={styles.flowLabel}>Ask</h3>
              <p className={styles.flowDesc}>Get instant answers from your data</p>
            </div>

            <div className={styles.flowArrow}>
              <svg viewBox="0 0 80 24" fill="none">
                <line x1="0" y1="12" x2="60" y2="12" stroke="var(--color-gold)" strokeWidth="2" />
                <path d="M60 6 L74 12 L60 18" fill="var(--color-gold)" />
              </svg>
            </div>

            <div className={`${styles.flowStage} ${styles.highlight}`}>
              <div className={styles.flowIcon}>
                <svg viewBox="0 0 48 48" fill="none">
                  <rect x="10" y="8" width="28" height="32" rx="2" fill="var(--color-gold)" opacity="0.2" stroke="var(--color-gold)" strokeWidth="2" />
                  <line x1="16" y1="16" x2="32" y2="16" stroke="var(--color-gold)" strokeWidth="2" strokeLinecap="round" />
                  <line x1="16" y1="22" x2="28" y2="22" stroke="var(--color-text-secondary)" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="16" y1="28" x2="30" y2="28" stroke="var(--color-text-secondary)" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M18 34 L22 38 L32 28" stroke="var(--color-gold)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3 className={styles.flowLabel}>Act</h3>
              <p className={styles.flowDesc}>Auto-generate work orders instantly</p>
            </div>
          </div>

          {/* Feature Details */}
          <div className={styles.featureDetails}>
            {featureDetails.map((feature, index) => (
              <div
                key={feature.title}
                className={`${styles.featureItem} ${expandedItems[index] ? styles.expanded : ''}`}
                onClick={() => toggleExpand(index)}
              >
                <div className={styles.featureHeader}>
                  <h4>{feature.title}</h4>
                  <span className={styles.expandIcon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </span>
                </div>
                <div className={styles.featureContent}>
                  <p>{feature.content}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className={styles.resourcesSection}>
        <div className="container">
          <div className="section-header">
            <div className="section-label">Our Solutions</div>
            <h2 className="section-title">We make your data easy to use</h2>
            <p className="section-subtitle">
              Powerful AI tools designed specifically for compressor companies to unlock the value in their data.
            </p>
          </div>
          <div className={styles.solutionsGrid}>
            {solutions.map((solution, index) => (
              <FeatureCard
                key={solution.title}
                title={solution.title}
                description={solution.description}
                animationDelay={index * 0.1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className={styles.resourcesSection} id="team">
        <div className="container">
          <div className="section-header">
            <div className="section-label">Our Team</div>
            <h2 className="section-title">Built by technical and financial talent</h2>
            <p className="section-subtitle">Experience spanning finance, machine learning, and software engineering.</p>
          </div>
          <div className={styles.teamGrid}>
            {teamMembers.map((member) => (
              <TeamCard
                key={member.name}
                avatar={member.avatar}
                name={member.name}
                role={member.role}
                detail={member.detail}
                linkedinUrl={member.linkedinUrl}
              />
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ResourcesPage;